import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import EuroIcon from '@mui/icons-material/Euro';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAuthStore } from '@/stores/authStore';
import { useDashboard } from '@/hooks';
import { useExpiringParts, useInventoryParts, type PartListItem } from '@/hooks/useInventory';
import { useMechanics } from '@/hooks/useStaffUsers';
import {
  RevenueChart,
  WorkOrderStatusChart,
  MechanicUtilizationChart,
  TodayScheduleWidget,
  RecentActivityFeed,
  AlertsWidget,
  QuickActionsWidget,
  PartsArrivingWidget,
  UninvoicedWidget,
  PartsBlockedWidget,
  AwaitingCustomerWidget,
  QuoteQueueWidget,
  CapacityWidget,
} from '@/components/dashboard';
import { DashboardWidgetFrame } from '@/components/dashboard/DashboardWidgetFrame';
import type { DashboardDialogId } from '@/components/dashboard/quickActions';
import { LowStockAlertWidget, ExpiringPartsWidget } from '@/components/inventory';
import { WorkOrderCreateModal } from '@/components/workorders/WorkOrderCreateModal';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { GlobalSearchDialog } from '@/components/layout/GlobalSearchDialog';
import type { InventoryItem, Mechanic } from '@/types';
import {
  DASHBOARD_WIDGETS,
  getAvailableWidgets,
  resolveDashboardRole,
  resolveDefaultVisibleIds,
  type DashboardRoleDefaults,
  type DashboardWidgetId,
} from './dashboardWidgets';
import { useDashboardLayout } from './useDashboardLayout';
import { useTenantSettings } from '@/hooks/useTenantSettings';

/**
 * Map a low-stock PartListItem to the InventoryItem shape LowStockAlertWidget
 * consumes. The widget only reads id/name/partNumber/quantity/minStockLevel/
 * supplier; the remaining fields are filled with safe defaults.
 */
function partListItemToInventoryItem(part: PartListItem): InventoryItem {
  return {
    id: part.id,
    partNumber: part.sku,
    name: part.name,
    category: 'other',
    categoryId: part.categoryId,
    categoryName: part.categoryName ?? undefined,
    unit: part.unit,
    isActive: part.isActive,
    unitPriceCents: part.sellPriceCents,
    costPriceCents: part.costPriceCents,
    quantity: part.totalQuantity,
    minStockLevel: part.reorderPoint,
    maxStockLevel: part.maxStockLevel ?? undefined,
    barcode: part.barcode ?? undefined,
    oemPartNumber: part.oemPartNumber ?? undefined,
    createdAt: part.createdAt,
    updatedAt: part.updatedAt,
  };
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  href?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

function MetricCard({ title, value, icon, color, loading, href, trend }: MetricCardProps) {
  const content = (
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(color, 0.14),
              color: color,
              '& svg': { fontSize: 28 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={32} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color }}>
                  {value}
                </Typography>
                {trend && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: trend.isPositive ? 'success.main' : 'error.main',
                      fontWeight: 500,
                    }}
                  >
                    {trend.isPositive ? '+' : ''}
                    {trend.value}%
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
  );

  // A real link so middle-click and "Open in new tab" work (#4108). A plain
  // left click still client-navigates to the same href as before.
  if (href) {
    return (
      <Card
        component={RouterLink}
        to={href}
        sx={{
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
        }}
      >
        {content}
      </Card>
    );
  }

  return <Card>{content}</Card>;
}

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const navigate = useNavigate();
  const theme = useTheme();

  const isManager = hasPermission('dashboard.view_all_stats');

  const { data, isLoading } = useDashboard();
  const {
    data: expiringParts,
    isLoading: isLoadingExpiring,
    refetch: refetchExpiring,
  } = useExpiringParts(30, { enabled: isManager });

  // Low-stock parts for the LowStockAlertWidget. The widget expects
  // InventoryItem[]; we fetch the low-stock page and map the fields it reads.
  const { data: lowStockPages, refetch: refetchLowStock } = useInventoryParts(
    { lowStock: true, limit: 20 },
    { enabled: isManager },
  );
  const lowStockItems = useMemo<InventoryItem[]>(
    () =>
      (lowStockPages?.pages ?? [])
        .flatMap((page) => page.data)
        .map(partListItemToInventoryItem),
    [lowStockPages],
  );

  // Mechanics for the create dialogs (work order / quotation / booking). Mirrors
  // the mapping used by WorkOrdersPage so the shape satisfies both modals.
  const { data: mechanicUsers = [] } = useMechanics();
  const mechanics = useMemo<Mechanic[]>(
    () =>
      mechanicUsers.map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phone: u.phone ?? '',
        skills: [],
        isAvailable: u.isActive,
      })),
    [mechanicUsers],
  );

  // Admin-configured per-role default layouts (optional) from tenant settings.
  const { tenantSettings } = useTenantSettings();
  const roleDefaults = tenantSettings?.dashboardRoleDefaults as
    | DashboardRoleDefaults
    | undefined;
  const dashboardRole = useMemo(() => resolveDashboardRole(hasPermission), [hasPermission]);

  const availableWidgets = useMemo(() => getAvailableWidgets(hasPermission), [hasPermission]);
  const availableWidgetIds = useMemo(
    () => availableWidgets.map((w) => w.id),
    [availableWidgets],
  );
  const defaultVisibleIds = useMemo(
    () => resolveDefaultVisibleIds(availableWidgets, dashboardRole, roleDefaults),
    [availableWidgets, dashboardRole, roleDefaults],
  );

  const layout = useDashboardLayout({ availableWidgetIds, defaultVisibleIds });

  const [editMode, setEditMode] = useState(false);
  const [activeDialog, setActiveDialog] = useState<DashboardDialogId | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const metrics = data?.metrics;

  const handleViewInventoryItem = (item: InventoryItem) => {
    void navigate(`/inventory?view=${item.id}`);
  };
  const handleOrderItem = (item: InventoryItem) => {
    void navigate(`/inventory/purchase-orders?newOrder=true&partId=${item.id}`);
  };
  const handleBulkOrder = (items: InventoryItem[]) => {
    const ids = items.map((i) => i.id).join(',');
    void navigate(`/inventory/purchase-orders?newOrder=true&partIds=${ids}`);
  };

  const renderWidget = (id: DashboardWidgetId): ReactNode => {
    switch (id) {
      case 'kpisManager':
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('todaysBookings')}
                value={metrics?.todaysBookings ?? 0}
                icon={<CalendarMonthIcon />}
                color={theme.palette.primary.main}
                loading={isLoading}
                href="/calendar"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('activeWorkOrders')}
                value={metrics?.activeWorkOrders ?? 0}
                icon={<AssignmentIcon />}
                color={theme.palette.success.dark}
                loading={isLoading}
                href="/workorders"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('pendingApprovals')}
                value={metrics?.pendingApprovals ?? 0}
                icon={<ThumbUpIcon />}
                color={theme.palette.warning.dark}
                loading={isLoading}
                href="/approvals"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('todaysRevenue')}
                value={`€${(Number(metrics?.todaysRevenueCents ?? 0) / 100).toFixed(2)}`}
                icon={<EuroIcon />}
                color={theme.palette.primary.main}
                loading={isLoading}
                href="/reports"
              />
            </Grid>
          </Grid>
        );
      case 'kpisMechanic':
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('myWorkOrders')}
                value={metrics?.myWorkOrders ?? 0}
                icon={<AssignmentIcon />}
                color={theme.palette.primary.main}
                loading={isLoading}
                href="/workorders"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('completedToday')}
                value={metrics?.completedToday ?? 0}
                icon={<BuildIcon />}
                color={theme.palette.success.dark}
                loading={isLoading}
                href="/workorders"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('hoursWorked')}
                value={`${metrics?.hoursWorkedToday ?? 0}h`}
                icon={<AccessTimeIcon />}
                color={theme.palette.warning.dark}
                loading={isLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <MetricCard
                title={t('pendingParts')}
                value={metrics?.pendingParts ?? 0}
                icon={<ThumbUpIcon />}
                color={theme.palette.secondary.dark}
                loading={isLoading}
                href="/inventory"
              />
            </Grid>
          </Grid>
        );
      case 'quickActions':
        return (
          <QuickActionsWidget
            onOpenDialog={setActiveDialog}
            enabledIds={layout.enabledQuickLinkIds}
            editMode={editMode}
            onToggleAction={layout.toggleQuickLink}
          />
        );
      case 'revenueChart':
        return <RevenueChart summary={data?.revenueSummary} daily={data?.revenue} />;
      case 'workOrderStatusChart':
        return (
          <Box sx={{ height: { xs: 340, md: 400 } }}>
            <WorkOrderStatusChart data={data?.workOrderStatuses} />
          </Box>
        );
      case 'lowStockAlert':
        return (
          <LowStockAlertWidget
            items={lowStockItems}
            onViewItem={handleViewInventoryItem}
            onOrderItem={handleOrderItem}
            onCreateBulkOrder={handleBulkOrder}
            onRefresh={() => void refetchLowStock()}
            maxItemsToShow={5}
          />
        );
      case 'expiringParts':
        return (
          <ExpiringPartsWidget
            items={expiringParts}
            isLoading={isLoadingExpiring}
            onRefresh={() => void refetchExpiring()}
            maxItemsToShow={5}
          />
        );
      case 'alerts':
        return (
          <Box sx={{ height: { xs: 340, md: 400 } }}>
            <AlertsWidget data={data?.alerts} />
          </Box>
        );
      case 'partsArriving':
        return <PartsArrivingWidget items={data?.partsArriving ?? []} />;
      case 'todaySchedule':
        return (
          <Box sx={{ height: { xs: 340, md: 400 } }}>
            <TodayScheduleWidget data={data?.todaySchedule} />
          </Box>
        );
      case 'mechanicUtilization':
        return (
          <Box sx={{ height: { xs: 340, md: 400 } }}>
            <MechanicUtilizationChart data={data?.mechanicUtilization} />
          </Box>
        );
      case 'recentActivity':
        return (
          <Box sx={{ height: { xs: 340, md: 400 } }}>
            <RecentActivityFeed data={data?.recentActivity} />
          </Box>
        );
      case 'uninvoiced':
        return <UninvoicedWidget data={data?.uninvoiced} />;
      case 'partsBlocked':
        return <PartsBlockedWidget items={data?.partsBlocked} />;
      case 'awaitingCustomer':
        return <AwaitingCustomerWidget items={data?.awaitingCustomer} />;
      case 'quoteQueue':
        return <QuoteQueueWidget data={data?.quoteQueue} />;
      case 'capacity':
        return (
          <Box sx={{ height: { xs: 380, md: 420 } }}>
            <CapacityWidget data={data?.capacity} />
          </Box>
        );
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('welcome', { name: user?.firstName })}
        </Typography>
        <Stack direction="row" gap={1}>
          {editMode && (
            <Button
              size="small"
              color="inherit"
              startIcon={<RestartAltIcon />}
              onClick={layout.resetToDefault}
            >
              {t('editor.reset')}
            </Button>
          )}
          <Button
            size="small"
            variant={editMode ? 'contained' : 'outlined'}
            startIcon={editMode ? <DoneIcon /> : <EditIcon />}
            onClick={() => setEditMode((v) => !v)}
          >
            {editMode ? t('editor.done') : t('editor.customize')}
          </Button>
        </Stack>
      </Stack>

      {editMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={layout.reorderWidgets}
        >
          <SortableContext items={layout.orderedWidgetIds} strategy={verticalListSortingStrategy}>
            {layout.orderedWidgetIds.map((id) => {
              const def = DASHBOARD_WIDGETS.find((w) => w.id === id);
              return (
                <DashboardWidgetFrame
                  key={id}
                  id={id}
                  label={def ? t(def.labelKey) : id}
                  hidden={layout.isWidgetHidden(id)}
                  onToggleHidden={() => layout.toggleWidget(id)}
                  interactive={id === 'quickActions'}
                >
                  {renderWidget(id)}
                </DashboardWidgetFrame>
              );
            })}
          </SortableContext>
        </DndContext>
      ) : (
        <Box sx={{ mt: 1 }}>
          {layout.orderedWidgetIds
            .filter((id) => !layout.isWidgetHidden(id))
            .map((id) => (
              <Box key={id} sx={{ mb: 3 }}>
                {renderWidget(id)}
              </Box>
            ))}
        </Box>
      )}

      {/* Create dialogs opened in place from quick actions */}
      <WorkOrderCreateModal
        open={activeDialog === 'workOrder' || activeDialog === 'quote'}
        onClose={() => setActiveDialog(null)}
        mechanics={mechanics}
        mode={activeDialog === 'quote' ? 'quote' : 'workOrder'}
      />
      <PersonFormDialog
        open={activeDialog === 'person'}
        onClose={() => setActiveDialog(null)}
        onCreated={() => setActiveDialog(null)}
      />
      <CompanyFormDialog
        open={activeDialog === 'company'}
        onClose={() => setActiveDialog(null)}
        onCreated={() => setActiveDialog(null)}
      />
      <GlobalSearchDialog
        open={activeDialog === 'search'}
        onClose={() => setActiveDialog(null)}
      />
    </Box>
  );
}
